import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import TextInput from 'react/elements/TextInput';
import SuggestingInput, { suggest } from 'react/elements/SuggestingInput';
import MaterialIcon from 'react/elements/MaterialIcon';
import { fetchProductPicture, setGroceryItemTags, updateGroceryItem } from 'redux/actions/groceryItems';
import useOffline, { NEEDS_CONNECTION_TITLE, onlineOnly, onlineOnlyField, useSaveWhenOnline } from '../../hooks/useOffline';

const FIELDS = [
	{ name: 'brand', label: 'Brand', placeholder: 'Who makes it' },
	{ name: 'product_url', label: 'Product link', placeholder: 'https://', type: 'url' },
	{ name: 'image_url', label: 'Image link', placeholder: 'https://', type: 'url' },
];

const fieldsOf = (item) => ({
	brand: item.brand || '',
	product_url: item.product_url || '',
	image_url: item.image_url || '',
});

const SUGGESTION_COUNT = 8;

/**
 * The budget's tags worth offering for what has been typed: those the item lacks, and when something
 * is typed, those containing it, the ones starting with it first. At most SUGGESTION_COUNT.
 */
export function tagSuggestions(budgetTags, itemTags, typed) {
	return suggest(budgetTags, itemTags, typed, SUGGESTION_COUNT);
}

/** The server's message for a refused save, or a general one. */
function errorMessage(error) {
	return error && error.responseJSON && error.responseJSON.error
		? error.responseJSON.error
		: 'That did not save. Try again.';
}

/**
 * What an item is, beyond its name, in the item editor: its tags, brand, and links to a picture of it
 * and to where it is sold. Each field saves when it is left, and a tag as soon as it is added or
 * removed. `details` is the item's details from the `grocery_item` domain, and `onSaved` is given the
 * answer to each save; `onError` a message when one is refused. `afterTags` goes between the tags
 * and the other details.
 *
 * A product link, once saved, is fetched for its picture (and its brand, if the item has none), and
 * the button beside it fetches again. Many shops refuse the fetch; the reason then shows under the
 * fields, and the image link can still be pasted by hand.
 *
 * Every save here is online-only, so offline the tag removes and fetch are disabled and the fields
 * read-only, titled "Needs a connection"; a field left offline saves once back online.
 */
export default function GroceryItemDetails({ details, onSaved, onError, afterTags = null }) {
	const dispatch = useDispatch();
	const item = details.item;
	const [fields, setFields] = useState(() => fieldsOf(item));
	const [newTag, setNewTag] = useState('');
	const offline = useOffline();
	const whenOnline = useSaveWhenOnline(offline);

	// A rename that merged this into another item shows that item instead.
	useEffect(() => {
		setFields(fieldsOf(item));
	}, [item.id]);

	const save = (request) => request.then((answer) => {
		onSaved(answer);
		return answer;
	}).catch((error) => {
		onError(errorMessage(error));
		return Promise.reject(error);
	});

	/**
	 * Only the field saved takes the answer's value. The others may be part way through being typed,
	 * and an answer arriving then must not put them back.
	 */
	const saveField = (name) => {
		const value = fields[name].trim();
		if(value === (item[name] || '')) {
			return;
		}
		const settle = (saved) => setFields((current) => ({ ...current, [name]: saved[name] || '' }));
		save(dispatch(updateGroceryItem(item.id, { [name]: value })))
			.then((answer) => {
				settle(answer.item);
				// A new product link brings its picture along, so nobody has to find the image link.
				if(name === 'product_url' && value) {
					fetchPicture();
				}
			}, () => settle(item));
	};

	// null, 'fetching', or { error } when the page gave no picture.
	const [pictureFetch, setPictureFetch] = useState(null);
	const fetchPicture = () => {
		setPictureFetch('fetching');
		dispatch(fetchProductPicture(item.id)).then((answer) => {
			onSaved(answer);
			// The picture and the brand are what a fetch can change.
			setFields((current) => ({ ...current, image_url: answer.item.image_url || '', brand: answer.item.brand || '' }));
			setPictureFetch(answer.fetch_error ? { error: answer.fetch_error } : null);
		}, (error) => setPictureFetch({ error: errorMessage(error) }));
	};

	const tags = item.tags || [];
	/**
	 * Each tag change sends the whole list, so one made before the last has answered is made to the
	 * list that one asked for, and waits for it: otherwise the second would drop the first.
	 */
	const tagSaves = useRef({ pending: 0, asked: null, last: Promise.resolve() });
	const latestTags = () => (tagSaves.current.pending ? tagSaves.current.asked : tags);
	const changeTags = (next) => {
		const saves = tagSaves.current;
		const send = () => save(dispatch(setGroceryItemTags(item.id, next))).catch(() => {});
		saves.last = (saves.pending ? saves.last.then(send) : send()).then(() => {
			saves.pending -= 1;
		});
		saves.pending += 1;
		saves.asked = next;
	};
	const addTag = (typed = newTag) => {
		const tag = typed.trim();
		setNewTag('');
		const current = latestTags();
		if(tag && !current.some((each) => each.toLowerCase() === tag.toLowerCase())) {
			changeTags([...current, tag]);
		}
	};
	const removeTag = (tag) => changeTags(latestTags().filter((each) => each !== tag));
	const suggestions = tagSuggestions(details.budget_tags || [], tags, newTag);
	const menuId = `grocery-item-tags-${item.id}`;

	return (
		<React.Fragment>
			<section className='meal_idea_panel__section grocery_item_details__tags'>
				<h6>Tags</h6>
				<div className='grocery_item_details__tag-list'>
					{tags.map((tag) =>
					<span key={tag} className='grocery_item_details__tag'>
						{tag}
						<button
							type='button'
							className='btn-flat grocery_item_details__tag-remove'
							aria-label={`Remove the tag ${tag}`}
							title={`Remove ${tag}`}
							{...onlineOnly(offline)}
							onClick={() => removeTag(tag)}
						>
							<MaterialIcon>close</MaterialIcon>
						</button>
					</span>
					)}
					<div className='input-field grocery_item_details__tag-input'>
						<SuggestingInput
							aria-label='Add a tag'
							placeholder={tags.length ? 'Add a tag' : 'Dairy, frozen, snacks...'}
							{...onlineOnlyField(offline)}
							value={newTag}
							onChange={setNewTag}
							suggestions={suggestions}
							onSubmit={addTag}
							submitKeys={['Enter', ',']}
							menuId={menuId}
							menuClassName='grocery_item_details__suggestions'
							onBlur={() => whenOnline('tag', () => addTag())}
						/>
					</div>
				</div>
			</section>

			{afterTags}

			<section className='meal_idea_panel__section grocery_item_details__fields'>
				<h6>Details</h6>
				{FIELDS.map((field) =>
				<label key={field.name} className='grocery_item_details__field'>
					<span className='grocery_item_details__label'>{field.label}</span>
					<span className='input-field'>
						<TextInput
							type={field.type || 'text'}
							aria-label={field.label}
							placeholder={field.placeholder}
							{...onlineOnlyField(offline)}
							value={fields[field.name]}
							onChange={(event) => {
								const value = event.target.value;
								setFields((current) => ({ ...current, [field.name]: value }));
							}}
							onBlur={() => whenOnline(field.name, () => saveField(field.name))}
							onKeyDown={(event) => {
								if(event.key === 'Enter') {
									event.preventDefault();
									event.target.blur();
								}
							}}
						/>
					</span>
					{field.name === 'product_url' && !!item.product_url &&
					<button
						type='button'
						className='btn-flat grocery_item_details__fetch'
						title={offline ? NEEDS_CONNECTION_TITLE : 'Get the picture from this page'}
						aria-label='Get the picture from this page'
						disabled={offline || pictureFetch === 'fetching'}
						onClick={fetchPicture}
					>
						<MaterialIcon>image_search</MaterialIcon>
					</button>
					}
					{field.name === 'product_url' && !!item.product_url &&
					<a
						className='grocery_item_details__open'
						href={item.product_url}
						target='_blank'
						rel='noopener noreferrer'
						title='Open the product page'
						aria-label='Open the product page'
					>
						<MaterialIcon>open_in_new</MaterialIcon>
					</a>
					}
				</label>
				)}
				{pictureFetch === 'fetching' &&
				<p className='grocery_item_details__fetch-status'>Getting the picture from the page...</p>
				}
				{!!pictureFetch && !!pictureFetch.error &&
				<p className='grocery_item_details__fetch-status is-error'>
					No picture from that page: {pictureFetch.error} An image link can be pasted instead.
				</p>
				}
			</section>
		</React.Fragment>
	);
}
